import { useEffect, useMemo, useState } from 'react';
import { Search, SlidersHorizontal, BarChart3, CalendarClock, Clock, ChevronRight } from 'lucide-react';
import { Input, Button, FilterResetButton, Spinner, cn } from '../../ui';
import { TONE, categoryMeta, type ReportDefinition, type Tone } from './reportKit';
import { matchesQuery, searchIndex, readRecent } from './reportPresentation';

/**
 * THE AREA'S HUE, CARRIED ONTO EVERYTHING THAT BELONGS TO IT ON THIS PAGE.
 *
 * The catalogue used to be eight shelves of identical black titles over grey text, told apart only by
 * a 20px icon and a word in each heading — finding the stock reports meant reading headings. Each area
 * now owns its colour on its filter chip, its recently-used chip and its whole shelf (edge, header
 * band, row hover), so "the gold block is stock" survives a glance.
 *
 * Literal class strings, one set per tone: Tailwind only emits a class it can find written out in
 * the source, so a hue interpolated into a class name would silently render nothing.
 *
 * The rows deliberately do NOT each repeat the area's icon — the same glyph down every row of a shelf
 * identifies nothing. The colour lives on the shelf; the rows take it on hover.
 */
const SHELF: Record<Tone, { band: string; hover: string; name: string; off: string; on: string }> = {
  brand: {
    band: 'from-brand-500/[0.15] via-brand-500/[0.05]',
    hover: 'hover:bg-brand-500/[0.07]',
    name: 'group-hover:text-brand-700 dark:group-hover:text-brand-300',
    off: 'border-brand-500/30 bg-brand-500/[0.06] hover:border-brand-500/60',
    on: 'border-brand-500 bg-brand-500/[0.18]',
  },
  sky: {
    band: 'from-sky-500/[0.15] via-sky-500/[0.05]',
    hover: 'hover:bg-sky-500/[0.07]',
    name: 'group-hover:text-sky-600 dark:group-hover:text-sky-400',
    off: 'border-sky-500/30 bg-sky-500/[0.06] hover:border-sky-500/60',
    on: 'border-sky-500 bg-sky-500/[0.18]',
  },
  harvest: {
    band: 'from-harvest-400/[0.20] via-harvest-400/[0.07]',
    hover: 'hover:bg-harvest-400/[0.10]',
    name: 'group-hover:text-harvest-700 dark:group-hover:text-harvest-300',
    off: 'border-harvest-400/40 bg-harvest-400/[0.08] hover:border-harvest-400/70',
    on: 'border-harvest-400 bg-harvest-400/[0.22]',
  },
  plum: {
    band: 'from-plum-500/[0.14] via-plum-500/[0.05]',
    hover: 'hover:bg-plum-500/[0.07]',
    name: 'group-hover:text-plum-600 dark:group-hover:text-plum-400',
    off: 'border-plum-500/30 bg-plum-500/[0.06] hover:border-plum-500/60',
    on: 'border-plum-500 bg-plum-500/[0.16]',
  },
  earth: {
    band: 'from-earth-500/[0.14] via-earth-500/[0.05]',
    hover: 'hover:bg-earth-500/[0.07]',
    name: 'group-hover:text-earth-600 dark:group-hover:text-earth-400',
    off: 'border-earth-500/30 bg-earth-500/[0.06] hover:border-earth-500/60',
    on: 'border-earth-500 bg-earth-500/[0.16]',
  },
  danger: {
    band: 'from-danger-500/[0.13] via-danger-500/[0.04]',
    hover: 'hover:bg-danger-500/[0.06]',
    name: 'group-hover:text-danger-600 dark:group-hover:text-danger-500',
    off: 'border-danger-500/30 bg-danger-500/[0.05] hover:border-danger-500/60',
    on: 'border-danger-500 bg-danger-500/[0.15]',
  },
  success: {
    band: 'from-success-500/[0.15] via-success-500/[0.05]',
    hover: 'hover:bg-success-500/[0.07]',
    name: 'group-hover:text-success-600 dark:group-hover:text-success-500',
    off: 'border-success-500/30 bg-success-500/[0.06] hover:border-success-500/60',
    on: 'border-success-500 bg-success-500/[0.18]',
  },
  teal: {
    band: 'from-teal-500/[0.15] via-teal-500/[0.05]',
    hover: 'hover:bg-teal-500/[0.07]',
    name: 'group-hover:text-teal-700 dark:group-hover:text-teal-300',
    off: 'border-teal-500/30 bg-teal-500/[0.06] hover:border-teal-500/60',
    on: 'border-teal-500 bg-teal-500/[0.18]',
  },
  indigo: {
    band: 'from-indigo-500/[0.14] via-indigo-500/[0.05]',
    hover: 'hover:bg-indigo-500/[0.07]',
    name: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-300',
    off: 'border-indigo-500/30 bg-indigo-500/[0.06] hover:border-indigo-500/60',
    on: 'border-indigo-500 bg-indigo-500/[0.16]',
  },
  // Only for a category the catalog invents before it is given a hue of its own.
  slate: {
    band: 'from-surface-3 via-surface-2/60',
    hover: 'hover:bg-surface-2',
    name: 'group-hover:text-content',
    off: 'border-line bg-surface hover:border-line-strong',
    on: 'border-line-strong bg-surface-3',
  },
};

/**
 * The landing page: find a report, open it.
 *
 * It used to be a gradient hero over a shelf of large cards beside a rail that listed the same 29
 * reports again — three ways of saying the same thing, and only about seven of them on screen at
 * once. This is search-first and dense: the whole catalogue is two or three scrolls at any size, and
 * typing a word anybody would use ("owes", "expiry", "dealer balance") narrows it immediately.
 */
export function ReportsHome({ reports, loading, area, onArea, onPick }: {
  reports: ReportDefinition[];
  loading?: boolean;
  /** The category filter, URL-backed by the page so a filtered catalogue is a link. */
  area?: string;
  onArea: (a: string | undefined) => void;
  onPick: (code: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [recentCodes, setRecentCodes] = useState<string[]>([]);
  useEffect(() => { setRecentCodes(readRecent()); }, [reports.length]);

  const index = useMemo(() => new Map(reports.map((r) => [r.code, searchIndex(r)])), [reports]);
  const matched = useMemo(
    () => reports.filter((r) => matchesQuery(r, query, index.get(r.code))),
    [reports, query, index],
  );
  const visible = useMemo(
    () => (area ? matched.filter((r) => r.category === area) : matched),
    [matched, area],
  );

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of reports) counts.set(r.category, (counts.get(r.category) ?? 0) + 1);
    return [...counts.entries()];
  }, [reports]);
  const matchedByCat = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of matched) m.set(r.category, (m.get(r.category) ?? 0) + 1);
    return m;
  }, [matched]);

  const grouped = useMemo(() => {
    const byCat = new Map<string, ReportDefinition[]>();
    for (const r of visible) byCat.set(r.category, [...(byCat.get(r.category) ?? []), r]);
    return [...byCat.entries()];
  }, [visible]);

  const recent = useMemo(() => {
    const known = new Map(reports.map((r) => [r.code, r]));
    return recentCodes.map((c) => known.get(c)).filter((r): r is ReportDefinition => !!r);
  }, [recentCodes, reports]);

  const narrowed = !!area || !!query.trim();

  return (
    <div className="min-w-0">
      {/* Title row — one line of identity, then straight into the search box. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line pb-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="grid size-10 shrink-0 place-items-center rounded-control bg-brand-100 text-brand-700 shadow-e1 dark:bg-brand-500/20 dark:text-brand-300"
            aria-hidden
          >
            <BarChart3 className="size-5" />
          </span>
          <div className="min-w-0">
            <h1 className="type-section-title text-content">Reports</h1>
            <p className="type-caption text-muted">
              Operational and business reporting across Green Bay
              {!loading && ` · ${reports.length} reports in ${categories.length} areas`}
            </p>
          </div>
        </div>
        <div className="w-full sm:w-[360px]">
          <Input
            allowClear
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search reports by name, category or purpose…"
            aria-label="Search reports"
            prefix={<Search size={15} className="text-subtle" />}
          />
        </div>
      </div>

      {/* Recently used — this browser only, and never in the way: it disappears the moment you search.
          Each chip wears its area's colour and icon, which differ chip to chip, so the eye can pick
          "the blue one" without reading. */}
      {!narrowed && recent.length > 0 && (
        <section className="mt-3">
          <h2 className="mb-1.5 flex items-center gap-1.5 type-overline text-subtle">
            <Clock className="size-3" /> Recently used
          </h2>
          <div className="flex flex-wrap gap-1.5">
            {recent.map((r) => {
              const m = categoryMeta(r.category);
              const Icon = m.icon;
              return (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => onPick(r.code)}
                  title={`${r.category} · ${r.name}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-sm font-medium text-content transition-[colors,box-shadow] hover:shadow-e1',
                    SHELF[m.tone].off,
                  )}
                >
                  <Icon className={cn('size-3.5 shrink-0', TONE[m.tone].ink)} aria-hidden />
                  {r.name}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Category filter — the same `area` parameter the URL carries. */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <FilterChip label="All" count={matched.length} on={!area} onClick={() => onArea(undefined)} />
        {categories.map(([cat]) => {
          const m = categoryMeta(cat);
          const n = matchedByCat.get(cat) ?? 0;
          return (
            <FilterChip
              key={cat}
              label={cat}
              count={n}
              dim={n === 0}
              tone={m.tone}
              icon={m.icon}
              on={area === cat}
              onClick={() => onArea(area === cat ? undefined : cat)}
            />
          );
        })}
        {narrowed && (
          <FilterResetButton onClick={() => { onArea(undefined); setQuery(''); }} />
        )}
      </div>

      {/* The catalogue itself — one card per area, in that area's colour. */}
      <div className="mt-4 space-y-4">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted"><Spinner /> Loading reports…</div>
        )}

        {!loading && !visible.length && (
          <div className="rounded-card border border-line bg-surface-2/50 px-4 py-10 text-center">
            <p className="text-sm text-content">Nothing matches “{query}”{area ? ` in ${area}` : ''}.</p>
            <p className="mt-1 type-caption text-muted">
              Search finds a report by its name, its area, what it answers, or a word people use for it.
            </p>
            <Button className="mt-3" size="sm" onClick={() => { setQuery(''); onArea(undefined); }}>Show all reports</Button>
          </div>
        )}

        {grouped.map(([cat, list]) => {
          const m = categoryMeta(cat);
          const t = TONE[m.tone];
          const s = SHELF[m.tone];
          const Icon = m.icon;
          return (
            <section
              key={cat}
              className={cn('overflow-hidden rounded-card border border-l-4 border-line bg-surface shadow-e1', t.railBorder)}
            >
              <h2
                className={cn(
                  'flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-line/70 bg-linear-to-r to-transparent px-3.5 py-2.5',
                  s.band,
                )}
              >
                <span className={cn('grid size-8 shrink-0 place-items-center rounded-control [&>svg]:size-4', t.chip)} aria-hidden>
                  <Icon />
                </span>
                <span className={cn('type-label-strong', t.ink)}>{cat}</span>
                <span className={cn('rounded-pill px-1.5 py-px type-caption font-medium tabular-nums', t.chip)}>
                  {list.length} {list.length === 1 ? 'report' : 'reports'}
                </span>
                {m.blurb && <span className="min-w-0 type-caption text-muted">{m.blurb}</span>}
              </h2>
              {/* `-mb-px` tucks the last row's rule under the card's own edge, so the shelf closes on one
                  line rather than two. */}
              <div className="-mb-px grid md:grid-cols-2 2xl:grid-cols-3">
                {list.map((r) => (
                  <button
                    key={r.code}
                    type="button"
                    onClick={() => onPick(r.code)}
                    className={cn(
                      'group flex min-w-0 items-start gap-2 border-b border-line/60 px-3.5 py-2.5 text-left transition-colors',
                      s.hover,
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className={cn('text-sm font-medium text-content transition-colors', s.name)}>{r.name}</span>
                        {/* What the report IS, in the area's own ink — these vary report to report. */}
                        <span className={cn('flex shrink-0 items-center gap-1.5 type-caption opacity-80', t.ink)}>
                          {r.pointInTime && (
                            <span title="Snapshot — as things stand today" className="inline-flex">
                              <CalendarClock className="size-3" aria-label="Snapshot" />
                            </span>
                          )}
                          {!!r.filters?.length && (
                            <span title={`${r.filters.length} filter${r.filters.length === 1 ? '' : 's'}`} className="inline-flex items-center gap-0.5">
                              <SlidersHorizontal className="size-3" />{r.filters.length}
                            </span>
                          )}
                          {r.chart && (
                            <span title="Has a chart" className="inline-flex">
                              <BarChart3 className="size-3" aria-label="Has a chart" />
                            </span>
                          )}
                        </span>
                      </span>
                      {/* Two lines, not one: cut at one, most of these stopped mid-sentence before
                          saying what the report answers. */}
                      <span className="mt-0.5 line-clamp-2 type-caption text-muted">{r.description}</span>
                    </span>
                    <ChevronRight className={cn('mt-1 size-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100', t.ink)} />
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/**
 * An area chip that ALWAYS wears its area's colour — resting as a light wash, selected as a strong
 * one. It used to be grey until clicked, and every selected chip turned the same brand green, so the
 * row of eight said nothing about which colour meant what until each was tried.
 */
function FilterChip({ label, count, on, tone, icon: Icon, dim, onClick }: {
  label: string; count: number; on: boolean; tone?: Tone; icon?: typeof BarChart3; dim?: boolean; onClick: () => void;
}) {
  const s = tone ? SHELF[tone] : null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 type-label text-content transition-[colors,box-shadow]',
        s
          ? (on ? cn(s.on, 'font-semibold shadow-e1') : s.off)
          : (on
            ? 'border-brand-500 bg-brand-500/[0.14] font-semibold shadow-e1'
            : 'border-line bg-surface hover:border-brand-300'),
        dim && !on && 'opacity-45',
      )}
    >
      {tone && Icon && <Icon className={cn('size-3.5 shrink-0', TONE[tone].ink)} aria-hidden />}
      {label}
      <span className={cn('tabular-nums', tone ? TONE[tone].ink : 'text-subtle')}>{count}</span>
    </button>
  );
}
